import { useCallback, useMemo } from 'react';
import { ModuleColumn, ModuleField } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/api';
import {
  applyTableColumnSettings,
  getDefaultFilterFields,
  getDefaultVisibleFields,
  getFilterableFieldOptions,
  sanitizeFilterFields,
  sanitizeVisibleFields,
} from '../utils/tableColumnSettings';

export function useTableColumns(
  moduleKey: string,
  allColumns: ModuleColumn[],
  formFields: ModuleField[] = [],
) {
  const { user, refreshUser } = useAuth();

  const defaultFields = useMemo(() => getDefaultVisibleFields(allColumns), [allColumns]);

  const filterOptions = useMemo(
    () => getFilterableFieldOptions(allColumns, formFields),
    [allColumns, formFields],
  );

  const defaultFilterFields = useMemo(
    () => sanitizeFilterFields(filterOptions, getDefaultFilterFields(moduleKey)),
    [filterOptions, moduleKey],
  );

  const activeFields = useMemo(() => {
    const saved = user?.company?.tableSettings?.[moduleKey]?.columns;
    if (saved?.length) return sanitizeVisibleFields(allColumns, saved);
    return defaultFields;
  }, [user?.company?.tableSettings, moduleKey, allColumns, defaultFields]);

  const activeFilterFields = useMemo(() => {
    const saved = user?.company?.tableSettings?.[moduleKey]?.filters;
    if (saved) return sanitizeFilterFields(filterOptions, saved);
    return defaultFilterFields;
  }, [user?.company?.tableSettings, moduleKey, filterOptions, defaultFilterFields]);

  const columns = useMemo(
    () => applyTableColumnSettings(allColumns, activeFields),
    [allColumns, activeFields],
  );

  const persist = useCallback(async (patch: { columns?: string[]; filters?: string[] }) => {
    const prev = (user?.company?.tableSettings?.[moduleKey] || {}) as { columns?: string[]; filters?: string[] };
    const tableSettings = {
      ...(user?.company?.tableSettings || {}),
      [moduleKey]: {
        columns: patch.columns ?? (prev.columns?.length ? prev.columns : activeFields),
        filters: patch.filters ?? (prev.filters ?? activeFilterFields),
      },
    };
    await settingsAPI.update({ tableSettings });
    await refreshUser();
  }, [user?.company?.tableSettings, moduleKey, activeFields, activeFilterFields, refreshUser]);

  const updateColumns = useCallback(async (fields: string[]) => {
    await persist({ columns: sanitizeVisibleFields(allColumns, fields) });
  }, [allColumns, persist]);

  const updateFilters = useCallback(async (fields: string[]) => {
    await persist({ filters: sanitizeFilterFields(filterOptions, fields) });
  }, [filterOptions, persist]);

  const resetColumns = useCallback(() => updateColumns(defaultFields), [defaultFields, updateColumns]);
  const resetFilters = useCallback(() => updateFilters(defaultFilterFields), [defaultFilterFields, updateFilters]);

  const isCustomized = useMemo(() => {
    const saved = user?.company?.tableSettings?.[moduleKey]?.columns;
    if (!saved?.length) return false;
    return JSON.stringify(saved) !== JSON.stringify(defaultFields);
  }, [user?.company?.tableSettings, moduleKey, defaultFields]);

  return {
    columns,
    allColumns,
    activeFields,
    defaultFields,
    updateColumns,
    resetColumns,
    isCustomized,
    filterOptions,
    activeFilterFields,
    defaultFilterFields,
    updateFilters,
    resetFilters,
  };
}
